const DEFAULT_VIEWPORT_PADDING = 8
const DEFAULT_GAP = 6

/**
 * Pure viewport positioning for portaled dropdown menus.
 * Anchors the menu directly to the trigger (above or below), shifting horizontally when needed.
 */
export function computeViewportDropdownPosition({
	triggerRect,
	menuHeight,
	menuWidth,
	viewportPadding = DEFAULT_VIEWPORT_PADDING,
	gap = DEFAULT_GAP,
	preferredPlacement = 'bottom',
	horizontalAlign = 'start',
	viewportWidth = window.innerWidth,
	viewportHeight = window.innerHeight,
	viewportOffsetTop = 0,
	viewportOffsetLeft = 0,
	menuZIndex = 2000,
}) {
	const safeMenuHeight = Math.max(menuHeight, 1)
	const safeMenuWidth = Math.max(menuWidth, 1)

	const viewTop = viewportOffsetTop + viewportPadding
	const viewBottom = viewportOffsetTop + viewportHeight - viewportPadding
	const viewLeft = viewportOffsetLeft + viewportPadding
	const viewRight = viewportOffsetLeft + viewportWidth - viewportPadding

	const spaceBelow = viewBottom - triggerRect.bottom
	const spaceAbove = triggerRect.top - viewTop

	const bottomEdgeIfOpenDown = triggerRect.bottom + gap + safeMenuHeight
	const topEdgeIfOpenUp = triggerRect.top - gap - safeMenuHeight

	const fitsBelow = bottomEdgeIfOpenDown <= viewBottom
	const fitsAbove = topEdgeIfOpenUp >= viewTop

	let placement = preferredPlacement === 'top' ? 'top' : 'bottom'
	if (fitsBelow && !fitsAbove) {
		placement = 'bottom'
	} else if (fitsAbove && !fitsBelow) {
		placement = 'top'
	} else if (!fitsBelow && !fitsAbove) {
		placement = spaceAbove >= spaceBelow ? 'top' : 'bottom'
	} else if (fitsBelow && fitsAbove) {
		placement = preferredPlacement === 'top' ? 'top' : 'bottom'
	}

	const availableVertical = (placement === 'top' ? spaceAbove : spaceBelow) - gap
	const maxHeight = Math.max(0, Math.min(safeMenuHeight, availableVertical))
	const needsScroll = safeMenuHeight > maxHeight

	let left =
		horizontalAlign === 'end' ? triggerRect.right - safeMenuWidth : triggerRect.left

	if (left + safeMenuWidth > viewRight) {
		left = viewRight - safeMenuWidth
	}
	if (left < viewLeft) {
		left = viewLeft
	}

	const style = {
		position: 'fixed',
		left,
		minWidth: safeMenuWidth,
		maxWidth: viewRight - viewLeft,
		maxHeight: needsScroll && maxHeight > 0 ? maxHeight : undefined,
		overflowY: needsScroll ? 'auto' : undefined,
		zIndex: menuZIndex,
		placement,
	}

	if (placement === 'top') {
		// Anchor menu bottom edge to trigger top edge.
		style.bottom = viewportOffsetTop + viewportHeight - triggerRect.top + gap
		style.top = undefined
	} else {
		// Anchor menu top edge to trigger bottom edge.
		style.top = triggerRect.bottom + gap
		style.bottom = undefined
	}

	return style
}

export function getViewportMetrics() {
	const visualViewport = window.visualViewport
	return {
		viewportWidth: visualViewport?.width ?? window.innerWidth,
		viewportHeight: visualViewport?.height ?? window.innerHeight,
		viewportOffsetTop: visualViewport?.offsetTop ?? 0,
		viewportOffsetLeft: visualViewport?.offsetLeft ?? 0,
	}
}

export function isTriggerRectVisible(
	triggerRect,
	viewportWidth = window.innerWidth,
	viewportHeight = window.innerHeight,
	viewportOffsetTop = 0,
) {
	const viewTop = viewportOffsetTop
	const viewBottom = viewportOffsetTop + viewportHeight
	return (
		triggerRect.bottom > viewTop &&
		triggerRect.top < viewBottom &&
		triggerRect.right > 0 &&
		triggerRect.left < viewportWidth
	)
}
